// Copyright (c) 2026 Goffy. All Rights Reserved.

export type RoundTeamLayout = {
  teamId: string;
  playerIds: string[];
};

export type RoundGroupLayout = {
  groupId: string;
  playerIds: string[];
  teams: RoundTeamLayout[];
};

export type RoundGroupsLayout = {
  roundId: string;
  baselinePlayerOrder: string[];
  effectivePlayerOrder: string[];
  groups: RoundGroupLayout[];
};

const MAX_GROUP_SIZE = 4;

export function buildRoundGroupsLayout(
  roundId: string,
  baselinePlayerOrder: ReadonlyArray<string>,
  manualPlayerOrder: ReadonlyArray<string> | null
): RoundGroupsLayout {
  const baseline = normalizeUniqueOrder(baselinePlayerOrder);
  const manual = normalizeUniqueOrder(manualPlayerOrder ?? []).filter((playerId) => baseline.includes(playerId));
  const manualSet = new Set(manual);
  const effectiveOrder = [...manual, ...baseline.filter((playerId) => !manualSet.has(playerId))];

  const groupSizes = resolveGroupSizes(effectiveOrder.length);
  const groups: RoundGroupLayout[] = [];
  let offset = 0;

  groupSizes.forEach((groupSize, groupIndex) => {
    const groupPlayers = effectiveOrder.slice(offset, offset + groupSize);
    offset += groupSize;

    const teamSizes = resolveTeamSizes(groupPlayers.length);
    const teams: RoundTeamLayout[] = [];
    let teamOffset = 0;
    teamSizes.forEach((teamSize, teamIndex) => {
      const teamPlayers = groupPlayers.slice(teamOffset, teamOffset + teamSize);
      teamOffset += teamSize;
      teams.push({
        teamId: `${roundId}:group:${groupIndex + 1}:team:${teamIndex + 1}`,
        playerIds: teamPlayers,
      });
    });

    groups.push({
      groupId: `${roundId}:group:${groupIndex + 1}`,
      playerIds: groupPlayers,
      teams,
    });
  });

  return {
    roundId,
    baselinePlayerOrder: baseline,
    effectivePlayerOrder: effectiveOrder,
    groups,
  };
}

export function applyPlayerSwap(
  playerOrder: ReadonlyArray<string>,
  sourcePlayerId: string,
  targetPlayerId: string
): string[] {
  if (!sourcePlayerId || !targetPlayerId || sourcePlayerId === targetPlayerId) {
    return [...playerOrder];
  }

  const next = [...playerOrder];
  const sourceIndex = next.indexOf(sourcePlayerId);
  const targetIndex = next.indexOf(targetPlayerId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return next;
  }

  next[sourceIndex] = targetPlayerId;
  next[targetIndex] = sourcePlayerId;
  return next;
}

function normalizeUniqueOrder(order: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  order.forEach((playerId) => {
    if (!playerId) return;
    if (seen.has(playerId)) return;
    seen.add(playerId);
    normalized.push(playerId);
  });
  return normalized;
}

function resolveGroupSizes(playerCount: number): number[] {
  if (playerCount <= 0) return [];
  if (playerCount === 1) return [1];

  const fullGroups = Math.floor(playerCount / MAX_GROUP_SIZE);
  const remainder = playerCount % MAX_GROUP_SIZE;
  const sizes = new Array(fullGroups).fill(MAX_GROUP_SIZE);

  if (remainder === 0) return sizes;
  if (remainder === 1) {
    if (fullGroups > 0) {
      return [...new Array(fullGroups - 1).fill(MAX_GROUP_SIZE), 3, 2];
    }
    return [1];
  }
  if (remainder === 2) return [...sizes, 2];
  return [...sizes, 3];
}

function resolveTeamSizes(groupSize: number): number[] {
  if (groupSize <= 0) return [];
  if (groupSize === 1) return [1];
  if (groupSize === 2) return [2];
  if (groupSize === 3) return [2, 1];
  if (groupSize === 4) return [2, 2];
  throw new Error(`Unsupported group size: ${groupSize}`);
}
